import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, MapPin, Calendar, Euro, Clock, Users,
  Search, CheckCircle2, Filter, X, FileText, Wrench, HardHat,
  ChevronDown, SlidersHorizontal, WifiOff, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { usePWA } from '../../../hooks/usePWA';

import { API_URL } from '../../../config/api';

// Chantier pipeline stages
const CHANTIER_STAGES = [
  { id: 'to_plan', name: 'A planifier', color: '#6B7280', icon: Calendar },
  { id: 'planned', name: 'Planifie', color: '#3B82F6', icon: Clock },
  { id: 'started', name: 'Demarre', color: '#F59E0B', icon: Wrench },
  { id: 'in_progress', name: 'En cours', color: '#8B5CF6', icon: HardHat },
  { id: 'completed', name: 'Termine', color: '#10B981', icon: CheckCircle2 },
];

// Filter options
const RENTABILITY_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: 'high', label: 'Haute' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'low', label: 'Faible' },
];

const AMOUNT_OPTIONS = [
  { value: '', label: 'Tous montants' },
  { value: 'small', label: '< 5 000 €' },
  { value: 'medium', label: '5 000 - 20 000 €' },
  { value: 'large', label: '> 20 000 €' },
];

export default function ChantierPipeline() {
  const { user } = useOutletContext();
  const { isOnline, cacheApiData } = usePWA();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOfflineData, setIsOfflineData] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    rentability: '',
    client: '',
    amountRange: '',
    dateFrom: '',
    dateTo: '',
  });

  // Get unique clients for filter dropdown
  const uniqueClients = useMemo(() => {
    const clients = [...new Set(chantiers.map(c => c.client_name).filter(Boolean))];
    return clients.sort();
  }, [chantiers]);

  // Calculate stats based on filtered chantiers
  const stats = useMemo(() => {
    const total = chantiers.length;
    const inProgress = chantiers.filter(c => ['started', 'in_progress'].includes(c.pipeline_stage || c.status)).length;
    const completed = chantiers.filter(c => (c.pipeline_stage || c.status) === 'completed').length;
    const toStart = chantiers.filter(c => ['to_plan', 'planned'].includes(c.pipeline_stage || c.status)).length;
    return { total, inProgress, completed, toStart };
  }, [chantiers]);

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return filters.rentability || filters.client || filters.amountRange || filters.dateFrom || filters.dateTo;
  }, [filters]);

  useEffect(() => {
    fetchChantiers();
    
    // Listen for coming back online
    const handleOnline = () => {
      fetchChantiers();
    };
    window.addEventListener('app-online', handleOnline);
    return () => window.removeEventListener('app-online', handleOnline);
  }, []);

  // Données DÉMO pour le mode sans authentification
  const DEMO_CHANTIERS = [
    { id: 'ch1', name: 'Rénovation SDB Dupont', client_name: 'M. Dupont', address: '12 rue des Lilas, Lyon', amount: 8500, pipeline_stage: 'to_plan', start_date: '2026-04-15', rentability: 'high' },
    { id: 'ch2', name: 'Installation chauffage Martin', client_name: 'Mme Martin', address: '45 av. Victor Hugo, Marseille', amount: 12000, pipeline_stage: 'planned', start_date: '2026-04-10', rentability: 'medium' },
    { id: 'ch3', name: 'Plomberie complète Villa Rosa', client_name: 'SCI Rosa', address: '8 chemin des Vignes, Nice', amount: 25000, pipeline_stage: 'started', start_date: '2026-04-01', rentability: 'high' },
    { id: 'ch4', name: 'Dépannage urgent Bernard', client_name: 'M. Bernard', address: '23 rue Nationale, Toulouse', amount: 350, pipeline_stage: 'in_progress', start_date: '2026-04-05', rentability: 'low' },
    { id: 'ch5', name: 'Réfection cuisine Lambert', client_name: 'Mme Lambert', address: '67 bd Gambetta, Bordeaux', amount: 15000, pipeline_stage: 'in_progress', start_date: '2026-03-20', rentability: 'high' },
    { id: 'ch6', name: 'Entretien annuel copro', client_name: 'Copro Les Mimosas', address: '100 av. de la Mer, Cannes', amount: 4500, pipeline_stage: 'completed', start_date: '2026-03-01', rentability: 'medium' },
    { id: 'ch7', name: 'Salle de bain PMR', client_name: 'M. Petit', address: '5 impasse des Roses, Lyon', amount: 9800, pipeline_stage: 'to_plan', start_date: '2026-04-20', rentability: 'high' },
    { id: 'ch8', name: 'Raccordement piscine', client_name: 'Mme Durand', address: '15 route du Château, Aix', amount: 6200, pipeline_stage: 'planned', start_date: '2026-04-12', rentability: 'medium' },
  ];

  const fetchChantiers = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO : Si pas de token, utiliser les données fictives
    if (!token) {
      setChantiers(DEMO_CHANTIERS);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/batiment/pipeline/chantier`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Check if response is from cache
      const isFromCache = response.headers.get('X-Offline-Cache') === 'true';
      setIsOfflineData(isFromCache);
      
      if (response.ok) {
        const data = await response.json();
        const allChantiers = [];
        if (data.data) {
          Object.entries(data.data).forEach(([stageId, stageChantiers]) => {
            stageChantiers.forEach(c => {
              allChantiers.push({ ...c, pipeline_stage: stageId });
            });
          });
        }
        setChantiers(allChantiers);
        
        // Cache the data for offline use
        if (!isFromCache && cacheApiData) {
          cacheApiData(`${API_URL}/api/batiment/pipeline/chantier`, data);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      // En cas d'erreur, utiliser les données démo
      setChantiers(DEMO_CHANTIERS);
      if (!isOnline) {
        toast.error('Mode hors ligne - Donnees de demonstration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMoveChantier = async (chantierId, newStageId) => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    setChantiers(prev => prev.map(c => 
      c.id === chantierId ? { ...c, pipeline_stage: newStageId, status: newStageId } : c
    ));

    try {
      const response = await fetch(`${API_URL}/api/batiment/pipeline/chantier/move?chantier_id=${chantierId}&new_stage=${newStageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Chantier deplace');
      } else {
        throw new Error('Move failed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du deplacement');
      fetchChantiers();
    }
  };

  const applyFilters = (chantier) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        chantier.name?.toLowerCase().includes(query) ||
        chantier.client_name?.toLowerCase().includes(query) ||
        chantier.address?.toLowerCase().includes(query) ||
        chantier.city?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }

    // Rentability filter
    if (filters.rentability && chantier.rentability !== filters.rentability) {
      return false;
    }

    // Client filter
    if (filters.client && chantier.client_name !== filters.client) {
      return false;
    }

    // Amount range filter
    if (filters.amountRange) {
      const amount = chantier.quote_amount || 0;
      switch (filters.amountRange) {
        case 'small':
          if (amount >= 5000) return false;
          break;
        case 'medium':
          if (amount < 5000 || amount > 20000) return false;
          break;
        case 'large':
          if (amount <= 20000) return false;
          break;
      }
    }

    // Date filters
    if (filters.dateFrom) {
      const chantierDate = new Date(chantier.planned_start_date || chantier.created_at);
      const filterDate = new Date(filters.dateFrom);
      if (chantierDate < filterDate) return false;
    }

    if (filters.dateTo) {
      const chantierDate = new Date(chantier.planned_start_date || chantier.created_at);
      const filterDate = new Date(filters.dateTo);
      if (chantierDate > filterDate) return false;
    }

    return true;
  };

  const getChantiersForStage = (stageId) => {
    return chantiers
      .filter(c => (c.pipeline_stage || c.status || 'to_plan') === stageId)
      .filter(applyFilters);
  };

  const clearFilters = () => {
    setFilters({
      rentability: '',
      client: '',
      amountRange: '',
      dateFrom: '',
      dateTo: '',
    });
    setSearchQuery('');
  };

  const handleDragStart = (e, chantier) => {
    setDraggedCard(chantier);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggedCard && (draggedCard.pipeline_stage || draggedCard.status || 'to_plan') !== stageId) {
      handleMoveChantier(draggedCard.id, stageId);
    }
    setDraggedCard(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getRentabilityColor = (rentability) => {
    switch (rentability) {
      case 'high': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="chantier-pipeline">
      {/* Offline indicator */}
      {isOfflineData && (
        <div className="bg-amber-100 text-amber-800 px-3 py-2 flex items-center justify-center gap-2 text-sm">
          <WifiOff className="w-4 h-4" />
          <span>Mode hors ligne - Donnees du cache</span>
          <button
            onClick={fetchChantiers}
            className="p-1 hover:bg-amber-200 rounded"
            disabled={!isOnline}
          >
            <RefreshCw className={`w-4 h-4 ${!isOnline ? 'opacity-50' : ''}`} />
          </button>
        </div>
      )}
      
      {/* Header - Responsive */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-3 sm:px-6 py-3 sm:py-4 rounded-t-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Title */}
          <div className="flex items-center gap-2 sm:gap-3">
            <HardHat className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
            <div>
              <h1 className="text-lg sm:text-2xl font-bold">Pipeline Chantiers</h1>
              <p className="text-white/80 text-xs sm:text-sm">{stats.total} chantiers</p>
            </div>
          </div>
          
          {/* Stats - Hidden on mobile, show on tablet+ */}
          <div className="hidden md:flex items-center gap-2">
            <span className="bg-white/20 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm" data-testid="stat-to-start">
              <span className="font-bold">{stats.toStart}</span> a demarrer
            </span>
            <span className="bg-purple-500/50 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm" data-testid="stat-in-progress">
              <span className="font-bold">{stats.inProgress}</span> en cours
            </span>
            <span className="bg-green-500/50 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm" data-testid="stat-completed">
              <span className="font-bold">{stats.completed}</span> termines
            </span>
          </div>

          {/* Mobile stats row */}
          <div className="flex md:hidden items-center justify-between gap-2 text-xs">
            <span className="bg-white/20 px-2 py-1 rounded-full">
              <span className="font-bold">{stats.toStart}</span> attente
            </span>
            <span className="bg-purple-500/50 px-2 py-1 rounded-full">
              <span className="font-bold">{stats.inProgress}</span> cours
            </span>
            <span className="bg-green-500/50 px-2 py-1 rounded-full">
              <span className="font-bold">{stats.completed}</span> finis
            </span>
          </div>

          {/* Search & Filter buttons */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-40 lg:w-48 pl-9 pr-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
                data-testid="search-input"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 sm:px-3 sm:py-2 rounded-lg border border-white/30 flex items-center gap-2 transition-colors min-h-[40px] min-w-[40px] justify-center ${
                showFilters || hasActiveFilters ? 'bg-white text-orange-600' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="filter-toggle-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Filtres</span>
              {hasActiveFilters && (
                <span className="w-2 h-2 bg-orange-500 rounded-full sm:hidden"></span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel - Collapsible */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-white border-b border-gray-200 overflow-hidden"
          >
            <div className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm sm:text-base">
                  <Filter className="w-4 h-4" />
                  Filtres avances
                </h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Effacer
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {/* Rentability Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Rentabilite</label>
                  <select
                    value={filters.rentability}
                    onChange={(e) => setFilters({ ...filters, rentability: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    data-testid="filter-rentability"
                  >
                    {RENTABILITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Client Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
                  <select
                    value={filters.client}
                    onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    data-testid="filter-client"
                  >
                    <option value="">Tous les clients</option>
                    {uniqueClients.map(client => (
                      <option key={client} value={client}>{client}</option>
                    ))}
                  </select>
                </div>

                {/* Amount Range Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Montant</label>
                  <select
                    value={filters.amountRange}
                    onChange={(e) => setFilters({ ...filters, amountRange: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    data-testid="filter-amount"
                  >
                    {AMOUNT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Date From Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date debut</label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    data-testid="filter-date-from"
                  />
                </div>

                {/* Date To Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    data-testid="filter-date-to"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Kanban Board - Responsive */}
      <div className="flex-1 overflow-x-auto bg-gray-100 p-2 sm:p-4">
        <div className="flex gap-2 sm:gap-4 h-full min-w-max pb-4">
          {CHANTIER_STAGES.map((stage) => {
            const stageChantiers = getChantiersForStage(stage.id);
            const StageIcon = stage.icon;
            return (
              <div
                key={stage.id}
                className="w-[280px] sm:w-80 flex-shrink-0 bg-gray-50 rounded-xl flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`stage-${stage.id}`}
              >
                {/* Stage Header */}
                <div className="p-3 sm:p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: stage.color + '20' }}
                      >
                        <StageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: stage.color }} />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{stage.name}</h3>
                    </div>
                    <span 
                      className="text-xs sm:text-sm font-medium px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: stage.color + '20', color: stage.color }}
                    >
                      {stageChantiers.length}
                    </span>
                  </div>
                </div>

                {/* Cards Container */}
                <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2 sm:space-y-3">
                  <AnimatePresence>
                    {stageChantiers.map((chantier) => (
                      <motion.div
                        key={chantier.id}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, chantier)}
                        onClick={() => { setSelectedChantier(chantier); setShowDetailModal(true); }}
                        className={`bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-200 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all active:scale-[0.98] ${
                          draggedCard?.id === chantier.id ? 'opacity-50 ring-2 ring-orange-400' : ''
                        }`}
                        data-testid={`chantier-card-${chantier.id}`}
                      >
                        {/* Card Header */}
                        <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
                          <h4 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 flex-1">
                            {chantier.name || chantier.address || 'Chantier sans nom'}
                          </h4>
                          {chantier.rentability && (
                            <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${getRentabilityColor(chantier.rentability)}`}>
                              {chantier.rentability === 'high' ? 'Rentable' : 
                               chantier.rentability === 'medium' ? 'Moyen' : 'Faible'}
                            </span>
                          )}
                        </div>
                        
                        {/* Client */}
                        {chantier.client_name && (
                          <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1.5 mb-1.5 sm:mb-2">
                            <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{chantier.client_name}</span>
                          </p>
                        )}
                        
                        {/* Address */}
                        {(chantier.address || chantier.city) && (
                          <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1.5 mb-1.5 sm:mb-2">
                            <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{chantier.address || chantier.city}</span>
                          </p>
                        )}

                        {/* Dates & Hours */}
                        <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3">
                          {chantier.planned_start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(chantier.planned_start_date)}
                            </span>
                          )}
                          {chantier.total_hours > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {chantier.total_hours}h
                            </span>
                          )}
                        </div>

                        {/* Footer with amount */}
                        <div className="pt-2 sm:pt-3 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs sm:text-sm font-bold text-orange-600">
                            {formatCurrency(chantier.quote_amount)}
                          </span>
                          {chantier.margin !== undefined && chantier.margin !== null && (
                            <span className={`text-[10px] sm:text-xs font-medium ${chantier.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {chantier.margin >= 0 ? '+' : ''}{formatCurrency(chantier.margin)}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Empty state */}
                  {stageChantiers.length === 0 && (
                    <div className="text-center py-6 sm:py-8 text-gray-400">
                      <Building2 className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-xs sm:text-sm">Aucun chantier</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail Modal */}
      <ChantierDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        chantier={selectedChantier}
        stages={CHANTIER_STAGES}
        onStageChange={handleMoveChantier}
      />
    </div>
  );
}

// ============ CHANTIER DETAIL MODAL - Responsive ============

function ChantierDetailModal({ isOpen, onClose, chantier, stages, onStageChange }) {
  if (!isOpen || !chantier) return null;

  const currentStage = stages.find(s => s.id === (chantier.pipeline_stage || chantier.status || 'to_plan'));
  const StageIcon = currentStage?.icon || Building2;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getRentabilityLabel = (rentability) => {
    switch (rentability) {
      case 'high': return { label: 'Tres rentable', color: 'bg-green-100 text-green-700' };
      case 'medium': return { label: 'Moyenne', color: 'bg-yellow-100 text-yellow-700' };
      case 'low': return { label: 'Faible', color: 'bg-red-100 text-red-700' };
      default: return { label: 'Non evalue', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const rentabilityInfo = getRentabilityLabel(chantier.rentability);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" data-testid="chantier-detail-modal">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="bg-white w-full sm:rounded-2xl sm:w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-t-2xl"
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                {chantier.name || chantier.address || 'Details du chantier'}
              </h2>
              {chantier.client_name && (
                <p className="text-gray-500 flex items-center gap-1.5 mt-1 text-sm">
                  <Users className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{chantier.client_name}</span>
                </p>
              )}
              <div className="flex items-center gap-2 mt-2 sm:mt-3 flex-wrap">
                <span 
                  className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium text-white flex items-center gap-1.5"
                  style={{ backgroundColor: currentStage?.color }}
                >
                  <StageIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  {currentStage?.name}
                </span>
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${rentabilityInfo.color}`}>
                  {rentabilityInfo.label}
                </span>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center" 
              data-testid="close-modal-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stage selector */}
        <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200">
          <label className="block text-xs font-medium text-gray-500 mb-2">Deplacer vers</label>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {stages.map(stage => {
              const Icon = stage.icon;
              const isActive = stage.id === (chantier.pipeline_stage || chantier.status || 'to_plan');
              return (
                <button
                  key={stage.id}
                  onClick={() => {
                    if (!isActive) {
                      onStageChange(chantier.id, stage.id);
                      onClose();
                    }
                  }}
                  className={`px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all flex items-center gap-1 sm:gap-1.5 min-h-[36px] ${
                    isActive
                      ? 'text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
                  }`}
                  style={isActive ? { backgroundColor: stage.color } : {}}
                  data-testid={`stage-btn-${stage.id}`}
                >
                  <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="hidden xs:inline">{stage.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Financial Info - 2x2 on mobile, 4 on desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <div className="p-3 sm:p-4 bg-orange-50 rounded-xl text-center">
                <p className="text-[10px] sm:text-xs text-orange-600 mb-1">Montant devis</p>
                <p className="font-bold text-orange-700 text-sm sm:text-lg">{formatCurrency(chantier.quote_amount)}</p>
              </div>
              <div className="p-3 sm:p-4 bg-blue-50 rounded-xl text-center">
                <p className="text-[10px] sm:text-xs text-blue-600 mb-1">Cout reel</p>
                <p className="font-bold text-blue-700 text-sm sm:text-lg">{formatCurrency(chantier.actual_cost)}</p>
              </div>
              <div className={`p-3 sm:p-4 rounded-xl text-center ${chantier.margin >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className={`text-[10px] sm:text-xs mb-1 ${chantier.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>Marge</p>
                <p className={`font-bold text-sm sm:text-lg ${chantier.margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(chantier.margin)}
                </p>
              </div>
              <div className="p-3 sm:p-4 bg-purple-50 rounded-xl text-center">
                <p className="text-[10px] sm:text-xs text-purple-600 mb-1">Heures</p>
                <p className="font-bold text-purple-700 text-sm sm:text-lg">{chantier.total_hours || 0}h</p>
              </div>
            </div>

            {/* Location Info */}
            {(chantier.address || chantier.city) && (
              <div className="p-3 sm:p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <p className="text-xs sm:text-sm font-medium text-gray-700">Adresse du chantier</p>
                </div>
                <p className="text-gray-900 text-sm sm:text-base">{chantier.address}</p>
                {chantier.city && <p className="text-gray-600 text-sm">{chantier.city} {chantier.postal_code}</p>}
              </div>
            )}

            {/* Dates Info */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div className="p-3 sm:p-4 bg-gray-50 rounded-xl">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Debut prevu</p>
                <p className="font-medium text-gray-900 text-sm">{formatDate(chantier.planned_start_date)}</p>
              </div>
              <div className="p-3 sm:p-4 bg-gray-50 rounded-xl">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Fin prevue</p>
                <p className="font-medium text-gray-900 text-sm">{formatDate(chantier.planned_end_date)}</p>
              </div>
              {chantier.actual_start_date && (
                <div className="p-3 sm:p-4 bg-green-50 rounded-xl">
                  <p className="text-[10px] sm:text-xs text-green-600 mb-1">Debut reel</p>
                  <p className="font-medium text-green-700 text-sm">{formatDate(chantier.actual_start_date)}</p>
                </div>
              )}
              {chantier.actual_end_date && (
                <div className="p-3 sm:p-4 bg-green-50 rounded-xl">
                  <p className="text-[10px] sm:text-xs text-green-600 mb-1">Fin reelle</p>
                  <p className="font-medium text-green-700 text-sm">{formatDate(chantier.actual_end_date)}</p>
                </div>
              )}
            </div>

            {/* Quote Reference */}
            {chantier.quote_number && (
              <div className="flex items-center gap-3 p-3 sm:p-4 bg-blue-50 rounded-xl">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-blue-600">Devis associe</p>
                  <p className="font-medium text-blue-700 truncate">{chantier.quote_number}</p>
                </div>
              </div>
            )}

            {/* Team */}
            {chantier.assigned_workers?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Users className="w-4 h-4" />
                  Equipe assignee
                </h4>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {chantier.assigned_workers.map((worker, idx) => (
                    <span key={idx} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-100 rounded-full text-xs sm:text-sm text-gray-700">
                      {worker}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Time entries */}
            {chantier.time_entries?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Clock className="w-4 h-4" />
                  Heures enregistrees
                </h4>
                <div className="space-y-2">
                  {chantier.time_entries.slice(-5).reverse().map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm truncate">{entry.worker_name}</p>
                        <p className="text-xs text-gray-500 truncate">{entry.description || 'Travail sur chantier'}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="font-bold text-purple-600 text-sm">{entry.hours}h</p>
                        <p className="text-xs text-gray-500">{formatDate(entry.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Costs */}
            {chantier.costs?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Euro className="w-4 h-4" />
                  Couts enregistres
                </h4>
                <div className="space-y-2">
                  {chantier.costs.slice(-5).reverse().map((cost, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm truncate">{cost.description}</p>
                        <p className="text-xs text-gray-500">{cost.category || 'Materiaux'}</p>
                      </div>
                      <p className="font-bold text-red-600 text-sm flex-shrink-0 ml-2">-{formatCurrency(cost.amount)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {chantier.documents?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <FileText className="w-4 h-4" />
                  Documents
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {chantier.documents.map((doc, idx) => (
                    <a 
                      key={idx}
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                    >
                      <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700 truncate">{doc.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
